<template>
    <app-modal :modal-id="modalId"
               :title="modalTitle"
               :preloader="preloader"
               modal-size="extra-large"
               @close-modal="closeModal">
        <template slot="header">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button type="button" class="close outline-none" @click.prevent="closeModal">
                <span>
                    <app-icon :name="'x'"></app-icon>
                </span>
            </button>
        </template>

        <template slot="body">
            <app-overlay-loader v-if="preloader" />
            <form ref="form"
                  class="mb-0"
                  :class="{ 'loading-opacity': preloader }"
                  :data-url="selectedUrl || 'incidents'">
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="incident-name">{{ $t('incident_title') }}</label>
                            <app-input id="incident-name"
                                       v-model="inputs.name"
                                       type="text"
                                       :required="true"
                                       :placeholder="$t('incident_title')" />
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-category">{{ $t('category') }}</label>
                            <select id="incident-category" v-model="inputs.category" class="form-control">
                                <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-type">{{ $t('incident_type') }}</label>
                            <select id="incident-type" v-model="inputs.type" class="form-control">
                                <option v-for="option in availableTypeOptions" :key="option.value" :value="option.value">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-severity">{{ $t('severity') }}</label>
                            <select id="incident-severity" v-model="inputs.severity" class="form-control">
                                <option v-for="option in severityOptions" :key="option.value" :value="option.value">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-status">{{ $t('status') }}</label>
                            <select id="incident-status" v-model="inputs.status" class="form-control">
                                <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-source">{{ $t('source') }}</label>
                            <select id="incident-source" v-model="inputs.source" class="form-control">
                                <option :value="null">{{ $t('select_source') }}</option>
                                <option v-for="option in sourceOptions" :key="option.value" :value="option.value">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-md-3">
                        <div class="form-group">
                            <label for="incident-delay">{{ $t('delay_minutes') }}</label>
                            <app-input id="incident-delay"
                                       v-model="inputs.delay_minutes"
                                       type="number"
                                       :min-number="0"
                                       :placeholder="$t('delay_minutes')" />
                        </div>
                    </div>

                    <div class="col-md-12">
                        <div class="incident-credit-note">
                            <div class="incident-credit-note__head">
                                <h6 class="mb-1">{{ $t('material_loss_follow_up') }}</h6>
                                <p class="mb-0 text-muted">{{ $t('material_loss_follow_up_hint') }}</p>
                            </div>

                            <div class="row">
                                <div class="col-md-4">
                                    <div class="form-group mb-md-0">
                                        <label for="incident-material-loss-status">{{ $t('confirmed_material_loss') }}</label>
                                        <select id="incident-material-loss-status" v-model="inputs.material_loss_status" class="form-control">
                                            <option value="">{{ $t('select_material_loss_status') }}</option>
                                            <option value="yes">{{ $t('yes') }}</option>
                                            <option value="no">{{ $t('no') }}</option>
                                            <option value="under_investigation">{{ $t('under_investigation') }}</option>
                                        </select>
                                    </div>
                                </div>

                                <div v-if="hasConfirmedMaterialLoss" class="col-md-4">
                                    <div class="form-group mb-md-0">
                                        <label for="incident-credit-note-generated">{{ $t('credit_note_generated') }}</label>
                                        <select id="incident-credit-note-generated" v-model="inputs.credit_note_generated" class="form-control">
                                            <option :value="false">{{ $t('no') }}</option>
                                            <option :value="true">{{ $t('yes') }}</option>
                                        </select>
                                    </div>
                                </div>

                                <div v-if="hasConfirmedMaterialLoss && inputs.credit_note_generated" class="col-md-4">
                                    <div class="form-group mb-0">
                                        <label for="incident-credit-note-reference">{{ $t('credit_note_reference') }}</label>
                                        <app-input id="incident-credit-note-reference"
                                                   v-model="inputs.credit_note_reference"
                                                   type="text"
                                                   :placeholder="$t('credit_note_reference')" />
                                    </div>
                                </div>

                                <div v-if="hasConfirmedMaterialLoss && inputs.credit_note_generated" class="col-md-4">
                                    <div class="form-group mb-0">
                                        <label for="incident-credit-note-generated-at">{{ $t('credit_note_generated_at') }}</label>
                                        <input id="incident-credit-note-generated-at"
                                               :value="inputs.credit_note_generated_at"
                                               type="datetime-local"
                                               class="form-control"
                                               @input="updateDateTimeField('credit_note_generated_at', $event.target.value)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="incident-trip">{{ $t('trip') }}</label>
                            <app-input id="incident-trip"
                                       v-model="tripSelection"
                                       type="advance-search-select"
                                       :loaded-per-time="5"
                                       fetch-url="incident-trip-options"
                                       list-value-field="label"
                                       :placeholder="$t('search_trip')" />
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="form-group">
                            <label for="incident-location-label">{{ $t('location_label') }}</label>
                            <app-input id="incident-location-label"
                                       v-model="inputs.location_label"
                                       type="text"
                                       :placeholder="$t('location_label')" />
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="form-group">
                            <label for="incident-latitude">{{ $t('latitude') }}</label>
                            <app-input id="incident-latitude"
                                       v-model="inputs.latitude"
                                       type="text"
                                       :placeholder="$t('latitude')" />
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="form-group">
                            <label for="incident-longitude">{{ $t('longitude') }}</label>
                            <app-input id="incident-longitude"
                                       v-model="inputs.longitude"
                                       type="text"
                                       :placeholder="$t('longitude')" />
                        </div>
                    </div>

                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="incident-occurred-at">{{ $t('occurred_at') }}</label>
                            <input id="incident-occurred-at"
                                   :value="inputs.occurred_at"
                                   type="datetime-local"
                                   class="form-control"
                                   @input="updateDateTimeField('occurred_at', $event.target.value)" />
                        </div>
                    </div>

                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="incident-reported-at">{{ $t('reported_at') }}</label>
                            <input id="incident-reported-at"
                                   :value="inputs.reported_at"
                                   type="datetime-local"
                                   class="form-control"
                                   @input="updateDateTimeField('reported_at', $event.target.value)" />
                        </div>
                    </div>

                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="incident-reporting">{{ $t('incident_reporting') }}</label>
                            <app-input id="incident-reporting"
                                       v-model="inputs.reporting"
                                       type="text-editor"
                                       :height="360"
                                       :allow-media="false"
                                       :placeholder="$t('incident_reporting')" />
                        </div>
                    </div>

                    <div class="col-md-6">
                        <div class="form-group">
                            <label for="incident-police-report">{{ $t('police_report') }}</label>
                            <app-input id="incident-police-report"
                                       v-model="policeReportFile"
                                       type="file"
                                       accept=".pdf,.png,.jpg,.jpeg"
                                       :label="$t('upload_police_report')"
                                       @change="setPoliceReport" />
                            <a v-if="existingPoliceReportUrl"
                               :href="existingPoliceReportUrl"
                               target="_blank"
                               class="incident-report-link">
                                {{ $t('view_police_report') }}
                            </a>
                        </div>
                    </div>

                    <div class="col-md-12">
                        <div class="form-group">
                            <div class="incident-attachments__header">
                                <label class="mb-0">{{ $t('additional_attachments') }}</label>
                                <div class="incident-attachments__picker">
                                    <app-input
                                        :key="attachmentUploaderKey"
                                        id="incident-attachments-batch"
                                        v-model="attachmentUploadBatch"
                                        type="file"
                                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.csv,.txt"
                                        :multiple="true"
                                        :label="$t('add_attachment')"
                                        @input="addAttachmentFiles"
                                    />
                                </div>
                            </div>

                            <div v-if="attachmentItems.length" class="incident-attachments">
                                <div
                                    v-for="(attachment, index) in attachmentItems"
                                    :key="attachment.localKey"
                                    class="incident-attachments__item"
                                >
                                    <div class="incident-attachments__grid">
                                        <div class="form-group mb-0">
                                            <label :for="`incident-attachment-name-${attachment.localKey}`">{{ $t('attachment_name') }}</label>
                                            <app-input
                                                :id="`incident-attachment-name-${attachment.localKey}`"
                                                v-model="attachment.name"
                                                type="text"
                                                :placeholder="$t('attachment_name')"
                                            />
                                        </div>

                                        <div class="incident-attachments__summary">
                                            <span class="incident-attachments__summary-label">{{ $t('document') }}</span>
                                            <strong>{{ attachment.fileName || attachment.name || '---' }}</strong>
                                        </div>

                                        <div class="incident-attachments__actions">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-danger"
                                                @click.prevent="removeAttachmentRow(index)"
                                            >
                                                <app-icon :name="'trash-2'" class="size-12 mr-1" />
                                                {{ $t('remove_attachment') }}
                                            </button>
                                        </div>
                                    </div>

                                    <a
                                        v-if="attachment.url"
                                        :href="attachment.url"
                                        target="_blank"
                                        class="incident-report-link incident-attachments__link"
                                    >
                                        {{ $t('view_document') }}
                                    </a>
                                </div>
                            </div>

                            <div v-else class="incident-attachments__empty">
                                {{ $t('no_attachment_added') }}
                            </div>
                        </div>
                    </div>

                </div>
            </form>
        </template>

        <template slot="footer">
            <button type="button" class="btn btn-secondary mr-3" data-dismiss="modal" @click="closeModal">
                {{ $t('cancel') }}
            </button>
            <button type="button" class="btn btn-primary" @click="submit">
                {{ $t('save') }}
            </button>
        </template>
    </app-modal>
</template>

<script>
import moment from 'moment';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import { ModalMixin } from '../../../Mixins/ModalMixin';

export default {
    name: 'IncidentAddEditModal',
    mixins: [FormMixin, ModalMixin],
    props: {
        tableId: {
            type: String,
        },
    },
    data() {
        return {
            preloader: false,
            modalId: 'incident-add-edit-modal',
            modalTitle: this.$t('new_incident'),
            tripSelection: null,
            policeReportFile: null,
            existingPoliceReportUrl: '',
            attachmentUploadBatch: [],
            attachmentUploaderKey: 0,
            attachmentItems: [],
            inputs: {
                id: null,
                name: '',
                category: 'hse',
                type: 'road_traffic_accident',
                severity: 'medium',
                status: 'reported',
                source: null,
                trip_id: null,
                latitude: '',
                longitude: '',
                location_label: '',
                occurred_at: '',
                reported_at: '',
                reporting: '',
                delay_minutes: null,
                material_loss_status: '',
                credit_note_generated: false,
                credit_note_reference: '',
                credit_note_generated_at: '',
            },
            categoryOptions: [
                { value: 'hse', label: 'incident_category_hse' },
                { value: 'security', label: 'incident_category_security' },
                { value: 'operations', label: 'incident_category_operations' },
            ],
            severityOptions: [
                { value: 'low', label: 'low' },
                { value: 'medium', label: 'medium' },
                { value: 'high', label: 'high' },
                { value: 'critical', label: 'critical' },
            ],
            statusOptions: [
                { value: 'reported', label: 'reported' },
                { value: 'acknowledged', label: 'acknowledged' },
                { value: 'in_progress', label: 'in_progress' },
                { value: 'resolved', label: 'resolved' },
                { value: 'closed', label: 'closed' },
            ],
            sourceOptions: [
                { value: 'driver', label: 'driver' },
                { value: 'escorter', label: 'escorter' },
                { value: 'control_room', label: 'control_room' },
                { value: 'tracker_alert', label: 'tracker_alert' },
                { value: 'partner', label: 'partner' },
                { value: 'border_post', label: 'border_post' },
            ],
            typeOptions: {
                hse: [
                    { value: 'road_traffic_accident', label: 'road_traffic_accident' },
                    { value: 'people_safety_incident', label: 'people_safety_incident' },
                    { value: 'driver_intoxication', label: 'driver_intoxication' },
                    { value: 'sop_non_compliance', label: 'sop_non_compliance' },
                    { value: 'near_miss', label: 'near_miss' },
                ],
                security: [
                    { value: 'theft_attempt', label: 'theft_attempt' },
                    { value: 'hijacking_attack', label: 'hijacking_attack' },
                    { value: 'cargo_loss', label: 'cargo_loss' },
                    { value: 'external_intrusion', label: 'external_intrusion' },
                ],
                operations: [
                    { value: 'mechanical_breakdown', label: 'mechanical_breakdown' },
                    { value: 'fuel_shortage', label: 'fuel_shortage' },
                    { value: 'toll_cash_shortage', label: 'toll_cash_shortage' },
                    { value: 'operational_delay', label: 'operational_delay' },
                    { value: 'documentation_issue', label: 'documentation_issue' },
                ],
            },
        };
    },
    computed: {
        availableTypeOptions() {
            return this.typeOptions[this.inputs.category] || [];
        },
        hasConfirmedMaterialLoss() {
            return this.inputs.material_loss_status === 'yes';
        },
    },
    watch: {
        'inputs.category'(value) {
            const nextOptions = this.typeOptions[value] || [];
            if (!nextOptions.find((option) => option.value === this.inputs.type)) {
                this.inputs.type = nextOptions.length ? nextOptions[0].value : null;
            }
        },
        'inputs.type'(value) {
            if (value === 'cargo_loss' && !this.inputs.material_loss_status) {
                this.inputs.material_loss_status = 'under_investigation';
            }
        },
        'inputs.material_loss_status'(value) {
            if (value === 'yes') {
                return;
            }
            this.inputs.credit_note_generated = false;
            this.inputs.credit_note_reference = '';
            this.inputs.credit_note_generated_at = '';
        },
    },
    methods: {
        beforeGetEditData() {
            this.preloader = true;
        },
        beforeSubmit() {
            this.preloader = true;
        },
        setPoliceReport(event) {
            this.policeReportFile = event.target.files[0] || null;
        },
        addAttachmentFiles(files) {
            (Array.isArray(files) ? files : [files]).filter(Boolean).forEach((file) => {
                this.attachmentItems.push(this.normalizeAttachmentItem({
                    file,
                    fileName: file.name,
                    name: this.deriveAttachmentName(file.name),
                }));
            });

            this.attachmentUploadBatch = [];
            this.attachmentUploaderKey += 1;
        },
        removeAttachmentRow(index) {
            this.attachmentItems.splice(index, 1);
        },
        normalizeAttachmentItem(attachment = {}) {
            return {
                localKey: attachment.id ? `existing-${attachment.id}` : `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                id: attachment.id || null,
                name: attachment.name || '',
                url: attachment.full_url || attachment.url || '',
                file: attachment.file || null,
                fileName: attachment.fileName || attachment.name || '',
            };
        },
        deriveAttachmentName(fileName) {
            return fileName ? fileName.replace(/\.[^.]+$/, '') : '';
        },
        updateDateTimeField(field, value) {
            this.$set(this.inputs, field, value || '');
        },
        submit() {
            const payload = new FormData();
            const submitData = {
                id: this.inputs.id,
                name: this.inputs.name,
                category: this.inputs.category,
                type: this.inputs.type,
                severity: this.inputs.severity,
                status: this.inputs.status,
                source: this.inputs.source,
                trip_id: this.tripSelection && this.tripSelection.id ? this.tripSelection.id : (this.inputs.trip_id || null),
                latitude: this.inputs.latitude,
                longitude: this.inputs.longitude,
                location_label: this.inputs.location_label,
                occurred_at: this.inputs.occurred_at,
                reported_at: this.inputs.reported_at,
                reporting: this.inputs.reporting,
                delay_minutes: this.inputs.delay_minutes,
                material_loss_status: this.inputs.material_loss_status || null,
                credit_note_generated: this.hasConfirmedMaterialLoss && this.inputs.credit_note_generated ? 1 : 0,
                credit_note_reference: this.hasConfirmedMaterialLoss ? this.inputs.credit_note_reference : null,
                credit_note_generated_at: this.hasConfirmedMaterialLoss ? this.inputs.credit_note_generated_at : null,
            };

            if (!this.isValidForm()) {
                return;
            }

            Object.entries(submitData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    payload.append(key, value);
                }
            });

            if (this.policeReportFile) {
                payload.append('police_report', this.policeReportFile);
            }

            let newAttachmentIndex = 0;
            this.attachmentItems.forEach((attachment) => {
                if (attachment.id) {
                    payload.append('existing_attachment_ids[]', attachment.id);
                    payload.append(`existing_attachment_names[${attachment.id}]`, attachment.name || '');
                    return;
                }

                if (!attachment.file) {
                    return;
                }

                payload.append(`attachments[${newAttachmentIndex}]`, attachment.file);
                payload.append(`attachment_names[${newAttachmentIndex}]`, attachment.name || this.deriveAttachmentName(attachment.file.name));
                newAttachmentIndex += 1;
            });

            this.beforeSubmit();

            if (this.selectedUrl) {
                payload.append('_method', 'patch');
            }

            const request = this.axiosPost({
                url: this.selectedUrl || 'incidents',
                data: payload,
            });

            request
                .then((response) => this.afterSuccess(response))
                .catch(({ response }) => {
                    if (response?.data?.message) {
                        this.$toastr.e(response.data.message);
                    }
                })
                .finally(() => this.afterFinalResponse());
        },
        afterSuccess(response) {
            this.$toastr.s(response.data.message);
            this.$hub.$emit(`reload-${this.tableId}`);
            this.closeModal();
        },
        afterFinalResponse() {
            this.preloader = false;
        },
        afterSuccessFromGetEditData(response) {
            const data = response.data;
            const {
                active,
                police_report,
                ...rest
            } = data;

            this.inputs = {
                ...this.inputs,
                ...rest,
                occurred_at: '',
                reported_at: '',
                material_loss_status: rest.material_loss_status || '',
                credit_note_generated: Boolean(rest.credit_note_generated),
                credit_note_reference: rest.credit_note_reference || '',
                credit_note_generated_at: '',
            };
            this.$nextTick(() => {
                this.updateDateTimeField('occurred_at', this.normalizeDateTimeInput(rest.occurred_at));
                this.updateDateTimeField('reported_at', this.normalizeDateTimeInput(rest.reported_at));
                this.updateDateTimeField('credit_note_generated_at', this.normalizeDateTimeInput(rest.credit_note_generated_at));
            });
            this.tripSelection = data.trip || null;
            this.existingPoliceReportUrl = data.police_report?.full_url || '';
            this.attachmentItems = Array.isArray(data.attachments)
                ? data.attachments.map((attachment) => this.normalizeAttachmentItem(attachment))
                : [];
            this.preloader = false;
        },
        normalizeDateTimeInput(value) {
            if (!value) {
                return '';
            }

            if (typeof value === 'string') {
                const directMatch = value.replace(' ', 'T').match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);

                if (directMatch) {
                    return directMatch[1];
                }
            }

            return moment(value).isValid() ? moment(value).format('YYYY-MM-DDTHH:mm') : '';
        },
    },
    created() {
        if (this.selectedUrl) {
            this.modalTitle = this.$t('edit_incident');
        }
    },
}
</script>

<style scoped>
.incident-report-link {
    display: inline-flex;
    margin-top: 0.6rem;
    font-size: 0.85rem;
    font-weight: 600;
}

.incident-attachments__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.85rem;
}

.incident-attachments__picker {
    width: 18rem;
    max-width: 100%;
}

.incident-attachments {
    display: grid;
    gap: 0.9rem;
}

.incident-attachments__item {
    padding: 0.95rem;
    border: 1px solid var(--default-border-color);
    background: var(--base-color);
}

.incident-attachments__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) auto;
    gap: 0.85rem;
    align-items: flex-end;
}

.incident-attachments__summary {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 2.875rem;
    padding: 0.7rem 0.9rem;
    border: 1px solid var(--default-border-color);
    background: var(--base-color);
}

.incident-attachments__summary-label {
    margin-bottom: 0.22rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--secondary-paragraph-color);
}

.incident-attachments__actions {
    display: flex;
    align-items: flex-end;
}

.incident-attachments__link {
    margin-top: 0.7rem;
}

.incident-attachments__empty {
    padding: 0.95rem;
    border: 1px dashed var(--default-border-color);
    color: var(--secondary-paragraph-color);
    background: var(--base-color);
}

.incident-credit-note {
    margin-bottom: 1rem;
    padding: 1rem;
    border: 1px solid var(--default-border-color);
    border-radius: 0.9rem;
    background: var(--base-color);
}

.incident-credit-note__head {
    margin-bottom: 0.9rem;
}

@media (max-width: 991.98px) {
    .incident-attachments__header {
        flex-direction: column;
        align-items: stretch;
    }

    .incident-attachments__picker {
        width: 100%;
    }

    .incident-attachments__grid {
        grid-template-columns: 1fr;
    }
}
</style>
